import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { ReferenceDataCombobox } from './ReferenceDataCombobox';
import { ShippingAddressPicker } from './ShippingAddressPicker';
import type { SalesOrderDto, ShippingAddressInput } from '../types/salesOrder.types';

interface Props {
  draft: SalesOrderDto;
  shippingAddress: ShippingAddressInput;
  onUpdateDraft: (fields: Partial<SalesOrderDto>) => void;
  onShippingAddressChange: (addr: ShippingAddressInput) => void;
  readOnly?: boolean;
}

// FO-SOI-3: editable SO-detail form. Contains the asterisked fields the SO POST
// requires + all optional flags + addresses + contacts. The FO-summary block
// (read-only) and intake/lines (separate components) live outside this form.
export function SalesOrderComposeForm({
  draft,
  shippingAddress,
  onUpdateDraft,
  onShippingAddressChange,
  readOnly,
}: Props) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">Sales Order Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label>PO Expiry Date <span className="text-destructive">*</span></Label>
            <Input
              type="date"
              value={draft.poExpiryDate ?? ''}
              onChange={(e) => onUpdateDraft({ poExpiryDate: e.target.value || null })}
              readOnly={readOnly}
            />
          </div>
          <div className="space-y-1">
            <Label>Delivery Due Date <span className="text-destructive">*</span></Label>
            <Input
              type="date"
              value={draft.deliveryDueDate ?? ''}
              onChange={(e) => onUpdateDraft({ deliveryDueDate: e.target.value || null })}
              readOnly={readOnly}
            />
          </div>
          <div className="space-y-1">
            <Label>Schedule</Label>
            <Input
              value={draft.scheduleId ?? ''}
              onChange={(e) => onUpdateDraft({ scheduleId: e.target.value || null })}
              placeholder="Day Time / 24 Hrs / etc."
              readOnly={readOnly}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <ReferenceDataCombobox
            source="stateProvinces"
            label="State / Province"
            value={draft.shippingStateProvinceId}
            onChange={(id) => onUpdateDraft({ shippingStateProvinceId: id })}
            required
            disabled={readOnly}
          />
          <ReferenceDataCombobox
            source="cityTowns"
            label="City / Town"
            value={draft.shippingCityTownId}
            onChange={(id) => onUpdateDraft({ shippingCityTownId: id })}
            required
            disabled={readOnly}
          />
        </div>

        <ShippingAddressPicker
          value={shippingAddress}
          onChange={onShippingAddressChange}
          readOnly={readOnly}
          // Same source as the readiness panel: addresses hang off the deliver-to company.
          customerId={draft.doCustCompanyId ?? draft.customerId ?? null}
          customerName={draft.doCustCompanyName ?? draft.customerName ?? 'this customer'}
          stateProvinceId={draft.shippingStateProvinceId}
          cityTownId={draft.shippingCityTownId}
        />

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Authorized Receivers</Label>
            <Textarea
              value={draft.authorizedReceivers ?? ''}
              onChange={(e) => onUpdateDraft({ authorizedReceivers: e.target.value || null })}
              rows={3}
              placeholder="Name + mobile per line"
              readOnly={readOnly}
            />
          </div>
          <div className="space-y-1">
            <Label>Other Instructions</Label>
            <Textarea
              value={draft.otherInstructions ?? ''}
              onChange={(e) => onUpdateDraft({ otherInstructions: e.target.value || null })}
              rows={3}
              readOnly={readOnly}
            />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <Label>Other Remarks (defaults from FO memo)</Label>
            <Textarea
              value={draft.soRemarks ?? ''}
              onChange={(e) => onUpdateDraft({ soRemarks: e.target.value || null })}
              rows={2}
              readOnly={readOnly}
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <ContactPair
            label="Office Contact 1"
            nameValue={draft.officeContact1}
            numberValue={draft.contactNumber1}
            onChange={(name, num) => onUpdateDraft({ officeContact1: name, contactNumber1: num })}
            readOnly={readOnly}
          />
          <ContactPair
            label="Office Contact 2"
            nameValue={draft.officeContact2}
            numberValue={draft.contactNumber2}
            onChange={(name, num) => onUpdateDraft({ officeContact2: name, contactNumber2: num })}
            readOnly={readOnly}
          />
          <ContactPair
            label="Office Contact 3"
            nameValue={draft.officeContact3}
            numberValue={draft.contactNumber3}
            onChange={(name, num) => onUpdateDraft({ officeContact3: name, contactNumber3: num })}
            readOnly={readOnly}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Sub-PO / Orderlist Link</Label>
            <Input
              value={draft.subPoOrderlistLink ?? ''}
              onChange={(e) => onUpdateDraft({ subPoOrderlistLink: e.target.value || null })}
              placeholder="https://…"
              readOnly={readOnly}
            />
          </div>
          <div className="space-y-1">
            <Label>Invoice To Letter</Label>
            <Input
              value={draft.invoiceToLetter ?? ''}
              onChange={(e) => onUpdateDraft({ invoiceToLetter: e.target.value || null })}
              readOnly={readOnly}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <FlagCheckbox label="Trailer" checked={draft.isTrailer} onChange={(v) => onUpdateDraft({ isTrailer: v })} readOnly={readOnly} />
          <FlagCheckbox label="M-F" checked={draft.isMf} onChange={(v) => onUpdateDraft({ isMf: v })} readOnly={readOnly} />
          <FlagCheckbox label="PPE Required" checked={draft.isPpeRequired} onChange={(v) => onUpdateDraft({ isPpeRequired: v })} readOnly={readOnly} />
          <FlagCheckbox label="Permit Required" checked={draft.isPermitRequired} onChange={(v) => onUpdateDraft({ isPermitRequired: v })} readOnly={readOnly} />
          <FlagCheckbox label="Priority" checked={draft.isPriority} onChange={(v) => onUpdateDraft({ isPriority: v })} readOnly={readOnly} />
          <FlagCheckbox label="Spot Order" checked={draft.isSpotOrder} onChange={(v) => onUpdateDraft({ isSpotOrder: v })} readOnly={readOnly} />
          <FlagCheckbox label="Reprint" checked={draft.isReprint} onChange={(v) => onUpdateDraft({ isReprint: v })} readOnly={readOnly} />
        </div>
      </CardContent>
    </Card>
  );
}

function ContactPair({
  label, nameValue, numberValue, onChange, readOnly,
}: {
  label: string;
  nameValue: string | null;
  numberValue: string | null;
  onChange: (name: string | null, num: string | null) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input
        value={nameValue ?? ''}
        onChange={(e) => onChange(e.target.value || null, numberValue)}
        placeholder="Name"
        readOnly={readOnly}
      />
      <Input
        value={numberValue ?? ''}
        onChange={(e) => onChange(nameValue, e.target.value || null)}
        placeholder="Mobile #"
        readOnly={readOnly}
      />
    </div>
  );
}

function FlagCheckbox({
  label, checked, onChange, readOnly,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <Checkbox
        checked={checked}
        onCheckedChange={(v) => !readOnly && onChange(v === true)}
        disabled={readOnly}
      />
      {label}
    </label>
  );
}
